import { Box, Divider, FormControl, MenuItem, Select, TextField } from "@mui/material"
import type { FC } from "react"
import { useEffect, useRef } from "react"
import { Controller, useFormContext } from "react-hook-form"

import { PRODUCT_TEST_SELECTABLE_STATUSES } from "../../constants"
import type { ProductTestFormValues } from "../../schemas"
import { calculateExpirationDate, getProductTestStatusLabel } from "../../utils"

import { AerosDatePicker } from "@/components/UiKit"
import {
  FIELD_ERROR_CLASSES,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
  TEXT_FIELD_STYLING_PROPS,
} from "@/constants/forms"
import type { ProductTestStatus } from "@/graphql/codegen/graphql"

interface ProductTestCoaFormProps {
  isCreateMode: boolean
  shelfLifeMonths: number
}

export const ProductTestCoaForm: FC<ProductTestCoaFormProps> = ({ isCreateMode, shelfLifeMonths }) => {
  const { control, setValue, trigger, watch } = useFormContext<ProductTestFormValues>()

  const formOrderId = watch("orderId")
  const initialOrderIdRef = useRef<string>()

  useEffect(() => {
    if (initialOrderIdRef.current === undefined) {
      initialOrderIdRef.current = formOrderId || ""
    }
  }, [formOrderId])

  return (
    <Box component='fieldset'>
      <legend className='font-medium tracking-[0.2px] text-gray-700'>COA Information</legend>
      <Divider className='mb-6 mt-1' />
      <Box className='flex flex-col gap-y-6'>
        <Box className='flex gap-6'>
          <Controller
            name='lotNumber'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box className='flex-1'>
                <label htmlFor='lot-number-input' className={FIELD_LABEL_CLASSES}>
                  Lot #<span className='text-red-600'>*</span>
                </label>
                <TextField
                  {...field}
                  {...TEXT_FIELD_STYLING_PROPS}
                  id='lot-number-input'
                  placeholder='Enter Lot #'
                  error={!!error}
                  disabled={!isCreateMode}
                  data-testid='lot-number-input'
                />
                {error && (
                  <p className={FIELD_ERROR_CLASSES} data-testid='lot-number-error'>
                    {error.message}
                  </p>
                )}
              </Box>
            )}
          />
          <Controller
            name='batchNumber'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box className='flex-1'>
                <label htmlFor='batch-number-input' className={FIELD_LABEL_CLASSES}>
                  Batch #
                </label>
                <TextField
                  {...field}
                  {...TEXT_FIELD_STYLING_PROPS}
                  id='batch-number-input'
                  placeholder='Enter Batch #'
                  error={!!error}
                  disabled={!isCreateMode}
                  data-testid='batch-number-input'
                />
                {error && (
                  <p className={FIELD_ERROR_CLASSES} data-testid='batch-number-error'>
                    {error.message}
                  </p>
                )}
              </Box>
            )}
          />
          <Controller
            name='orderId'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box className='flex-1'>
                <label htmlFor='po-number-input' className={FIELD_LABEL_CLASSES}>
                  Purchase Order #
                </label>
                <TextField
                  {...field}
                  {...TEXT_FIELD_STYLING_PROPS}
                  id='po-number-input'
                  placeholder='Enter PO #'
                  error={!!error}
                  data-testid='po-number-input'
                />
                {error && (
                  <p className={FIELD_ERROR_CLASSES} data-testid='po-number-error'>
                    {error.message}
                  </p>
                )}
              </Box>
            )}
          />
        </Box>
        <Box className='flex gap-6'>
          <Controller
            name='manufacturingDate'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box className='flex-1'>
                <label id='manufacturing-date-label' className={FIELD_LABEL_CLASSES}>
                  Manufacturing Date<span className='text-red-600'>*</span>
                </label>
                <AerosDatePicker
                  {...field}
                  onChange={(value) => {
                    field.onChange(value)
                    if (shelfLifeMonths && value) {
                      setValue("expirationDate", calculateExpirationDate(value, shelfLifeMonths))
                    }
                    trigger("expirationDate")
                  }}
                  error={!!error}
                  helperText={error?.message}
                  required
                  data-testid='manufacturing-date-picker'
                />
              </Box>
            )}
          />
          <Controller
            name='expirationDate'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box className='flex-1'>
                <label id='expiration-date-label' className={FIELD_LABEL_CLASSES}>
                  Expiration Date<span className='text-red-600'>*</span>
                </label>
                <AerosDatePicker
                  {...field}
                  onChange={(value) => {
                    field.onChange(value)
                    trigger("manufacturingDate")
                  }}
                  error={!!error}
                  helperText={error?.message}
                  required
                  data-testid='expiration-date-picker'
                />
              </Box>
            )}
          />
          <Controller
            name='status'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl className='flex-1'>
                <label id='status-label' className={FIELD_LABEL_CLASSES}>
                  Status<span className='text-red-600'>*</span>
                </label>
                <Select
                  {...field}
                  {...SELECT_STYLING_PROPS}
                  labelId='status-label'
                  error={!!error}
                  renderValue={(selected) =>
                    selected ? (
                      getProductTestStatusLabel(selected as ProductTestStatus)
                    ) : (
                      <span className='text-gray-500'>Select Test Results Status</span>
                    )
                  }
                  data-testid='status-select'
                >
                  {PRODUCT_TEST_SELECTABLE_STATUSES.map((value) => (
                    <MenuItem key={value} value={value} data-test='status-option'>
                      {getProductTestStatusLabel(value)}
                    </MenuItem>
                  ))}
                </Select>
                {error && (
                  <p className={FIELD_ERROR_CLASSES} data-testid='status-error'>
                    {error.message}
                  </p>
                )}
              </FormControl>
            )}
          />
        </Box>
      </Box>
    </Box>
  )
}
